import { formatQualityLabel } from '@omni/kit/components/KitPlayer/utils/formatQualityLabel';
import Hls, {
  ErrorData,
  Events,
  LevelLoadedData,
  LevelSwitchedData,
  LevelSwitchingData,
  ManifestParsedData,
  MediaAttachedData,
  MediaPlaylist,
} from 'hls.js';
import { useCallback, useRef } from 'react';

import { PlayerVideoProps, QualityLevel } from '../PlayerVideo.types';

const debug = require('debug')(
  'tca:KitPlayer:components:PlayerVideo:hooks:useHlsEvents'
);

type useHlsEventsProps = {
  onError: PlayerVideoProps['onError'];
  onBufferStart: PlayerVideoProps['onBufferStart'];
  onBufferEnd: PlayerVideoProps['onBufferEnd'];
  onLiveDetected: PlayerVideoProps['onLiveDetected'];
  subtitleTracksRef: React.MutableRefObject<MediaPlaylist[]>;
  qualityLevelsRef: React.MutableRefObject<QualityLevel[]>;
  isManuallySwitchingLevelsRef: React.MutableRefObject<boolean>;
  currentQualityLevelRef: React.MutableRefObject<number>;
  currentQualityLabelRef: React.MutableRefObject<string>;
};

interface UseHlsEventsReturn {
  attachHlsEvents: (hls: Hls) => () => void;
}

/**
 * useHlsEvents hook handles all HLS run time events
 *
 * https://github.com/video-dev/hls.js/blob/master/docs/API.md#runtime-events
 */
export default ({
  onError,
  onBufferStart,
  onBufferEnd,
  onLiveDetected,
  subtitleTracksRef,
  qualityLevelsRef,
  isManuallySwitchingLevelsRef,
  currentQualityLevelRef,
  currentQualityLabelRef,
}: useHlsEventsProps): UseHlsEventsReturn => {
  const persistQualityLevelRef = useRef<number | undefined>(undefined);

  const getQualityLevels = useCallback((hls: Hls): QualityLevel[] => {
    const levels: QualityLevel[] = [{ label: 'Auto', level: -1 }];

    if (hls.levels && hls.levels.length) {
      levels.push(
        ...hls.levels.map((level, index) => ({
          label: formatQualityLabel(hls, index),
          level: index,
        }))
      );
    }

    return levels.reverse();
  }, []);

  const onMedaiAttachedEvent = useCallback(
    (_: Events.MEDIA_ATTACHED, data: MediaAttachedData) => {
      debug('video and hls.js are now bound together !', data);
    },
    []
  );

  const onManifestParsedEvent = useCallback(
    async (hls: Hls, _: Events.MANIFEST_PARSED, data: ManifestParsedData) => {
      debug('manifest parsed', data);
      subtitleTracksRef.current = data.subtitleTracks;
      qualityLevelsRef.current = getQualityLevels(hls);
      hls.subtitleTrack = -1;

      // Restore the previously selected quality level if it exists
      if (
        typeof persistQualityLevelRef.current === 'number' &&
        hls.currentLevel !== persistQualityLevelRef.current
      ) {
        debug('Restoring quality level:', persistQualityLevelRef.current);
        hls.currentLevel = persistQualityLevelRef.current;
        currentQualityLevelRef.current = persistQualityLevelRef.current;
        currentQualityLabelRef.current = formatQualityLabel(
          hls,
          persistQualityLevelRef.current
        );
      }
    },
    [
      currentQualityLabelRef,
      currentQualityLevelRef,
      getQualityLevels,
      qualityLevelsRef,
      subtitleTracksRef,
    ]
  );

  const onErrorEvent = useCallback(
    (hls: Hls, _: Events.ERROR, data: ErrorData) => {
      debug('hls error', data);
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            hls.startLoad();
            break;
          default:
            onError?.({ error: data.type });
            break;
        }
      }
    },
    [onError]
  );

  const onLevelLoadedEvent = useCallback(
    (_: Events.LEVEL_LOADED, data: LevelLoadedData) => {
      debug('level loaded', data);
      onLiveDetected?.(data.details.live);
    },
    [onLiveDetected]
  );

  const onLevelSwitchingEvent = useCallback(
    (_: Events.LEVEL_SWITCHING, data: LevelSwitchingData) => {
      debug('level switching', data);
      if (isManuallySwitchingLevelsRef.current) {
        onBufferStart?.();
      }
    },
    [isManuallySwitchingLevelsRef, onBufferStart]
  );

  const onLevelSwitchedEvent = useCallback(
    (hls: Hls, _: Events.LEVEL_SWITCHED, data: LevelSwitchedData) => {
      debug('level switched', data);
      if (hls.autoLevelEnabled) {
        currentQualityLabelRef.current = `Auto • ${formatQualityLabel(
          hls,
          hls.currentLevel
        )}`;
        currentQualityLevelRef.current = -1;
        persistQualityLevelRef.current = -1;
      } else {
        currentQualityLabelRef.current = formatQualityLabel(
          hls,
          hls.currentLevel
        );
        currentQualityLevelRef.current = hls.currentLevel;
        persistQualityLevelRef.current = hls.currentLevel;
      }

      if (isManuallySwitchingLevelsRef.current) {
        isManuallySwitchingLevelsRef.current = false;
      }

      onBufferEnd?.();
    },
    [
      currentQualityLabelRef,
      currentQualityLevelRef,
      isManuallySwitchingLevelsRef,
      onBufferEnd,
    ]
  );

  const attachHlsEvents = useCallback(
    (hls: Hls) => {
      const boundedOnManifestParsedEvent = onManifestParsedEvent.bind(
        this,
        hls
      );
      const boundedOnErrorEvent = onErrorEvent.bind(this, hls);
      const boundedOnLevelSwitchedEvent = onLevelSwitchedEvent.bind(this, hls);

      hls.on(Hls.Events.MEDIA_ATTACHED, onMedaiAttachedEvent);
      hls.once(Hls.Events.MANIFEST_PARSED, boundedOnManifestParsedEvent);
      hls.on(Hls.Events.ERROR, boundedOnErrorEvent);
      hls.once(Hls.Events.LEVEL_LOADED, onLevelLoadedEvent);
      hls.on(Hls.Events.LEVEL_SWITCHING, onLevelSwitchingEvent);
      hls.on(Hls.Events.LEVEL_SWITCHED, boundedOnLevelSwitchedEvent);

      return () => {
        // Save the current quality level before detaching events
        if (!hls.autoLevelEnabled) {
          persistQualityLevelRef.current = hls.currentLevel;
        }

        hls.off(Hls.Events.MEDIA_ATTACHED, onMedaiAttachedEvent);
        hls.off(Hls.Events.MANIFEST_PARSED, boundedOnManifestParsedEvent);
        hls.off(Hls.Events.ERROR, boundedOnErrorEvent);
        hls.off(Hls.Events.LEVEL_LOADED, onLevelLoadedEvent);
        hls.off(Hls.Events.LEVEL_SWITCHING, onLevelSwitchingEvent);
        hls.off(Hls.Events.LEVEL_SWITCHED, boundedOnLevelSwitchedEvent);
      };
    },
    [
      onErrorEvent,
      onLevelLoadedEvent,
      onLevelSwitchedEvent,
      onLevelSwitchingEvent,
      onManifestParsedEvent,
      onMedaiAttachedEvent,
    ]
  );

  return {
    attachHlsEvents,
  };
};
